<template>
  <div class="channel-history-page">
    <!-- 头部 -->
    <div class="channel-history-header">
      <van-icon name="arrow-left" size="20" color="#333" style="cursor: pointer;" @click="$router.back()" />
      <span class="channel-history-title">{{ $t('channelHistory_myMessages') }}</span>
      <van-icon name="search" size="20" color="#333" style="cursor: pointer;" @click="goToSearch" />
    </div>

    <!-- 消息列表 -->
    <div class="channel-history-list">
      <div v-if="loading && sessions.length === 0" style="display: flex; justify-content: center; padding: 40px;">
        <van-loading size="24" />
      </div>

      <div v-for="session in sessions" :key="session.chatSessionId" class="history-session-item"
        @click="goToSession(session)">
        <!-- User Msg（优先展示，作为主内容） -->
        <div v-if="session.userMsg" class="history-session-usermsg">{{ $common.formatUserMsgPreview(session.userMsg) }}</div>
        <!-- AI Summary（缩小 + 特殊标识） -->
        <div v-if="session.aiSummary" :class="['history-session-ai-summary', session.userMsg ? 'has-user-msg' : '']">
          <span class="history-session-ai-icon">✨</span>
          <span class="history-session-ai-text">{{ session.aiSummary }}</span>
        </div>
        <!-- Status badges -->
        <div class="history-session-badges">
          <span v-if="session.public != 1" class="history-badge-private">{{ $t('badge_only_me') }}</span>
          <span v-if="session.expired == 1" class="history-badge-expired">{{ $t('badge_expired') }}</span>
        </div>
        <!-- Footer -->
        <div class="history-session-footer">
          <img v-if="currentUser.avatar" :src="$common.getFullImageUrl(currentUser.avatar, $config, 's')" class="history-session-avatar" />
          <div v-else class="history-session-avatar-placeholder">{{ (currentUser.userName || 'M')[0] }}</div>
          <span class="history-session-name">{{ currentUser.userName || $t('common_me') }}</span>
          <span v-if="session.createdAt" class="history-session-time">{{ $common.formatSmartTime(session.createdAt, $i18n.locale) }}</span>
        </div>
      </div>

      <div v-if="sessions.length === 0 && !loading && hasLoaded" class="channel-empty-state">
        <div class="channel-empty-icon">
          <div class="channel-empty-icon-bg">
            <van-icon name="chat" size="40" color="#fff" />
          </div>
          <div class="channel-empty-icon-sparkle">
            <van-icon name="star-o" size="14" color="#E8543A" />
          </div>
        </div>
        <div class="channel-empty-title">{{ $t('channelEmpty_title') }}</div>
        <div class="channel-empty-subtitle">{{ $t('channelEmpty_subtitle') }}</div>
        <div class="channel-empty-hint">
          <van-icon name="star-o" size="10" color="#E8543A" class="channel-empty-hint-icon" />
          <span>{{ $t('channelEmpty_hint') }}</span>
        </div>
      </div>

      <div v-if="loading && hasMore && sessions.length > 0" style="text-align: center; padding: 16px;">
        <van-loading size="20" />
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Vue from 'vue'
import { Icon, Toast, Empty, Loading } from 'vant'

Vue.use(Icon)
Vue.use(Toast)
Vue.use(Empty)
Vue.use(Loading)

export default {
  name: 'ChannelHistoryChats',
  data() {
    return {
      channelId: '',
      sessions: [],
      loading: false,
      hasLoaded: false,
      hasMore: true,
      curPage: 1,
      pageSize: 20
    }
  },
  computed: {
    currentUser() {
      return this.$store.state.user || {}
    }
  },
  watch: {
    '$route.params.id': {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.channelId = String(newVal)
          this.refresh()
        }
      }
    }
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll)
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll)
  },
  methods: {
    async refresh() {
      this.curPage = 1
      this.sessions = []
      this.hasMore = true
      await this.loadSessions()
    },
    async loadSessions() {
      if (this.loading) return
      this.loading = true
      try {
        const response = await axios.post('/api/chat/pull_chat_sessions', {
          channelId: this.channelId,
          curPage: this.curPage,
          pageSize: this.pageSize
        })
        if (response.data.code === 0 && response.data.data) {
          const sessions = (response.data.data.chatSessions || []).map(s => this.normalizeSession(s))
          if (this.curPage === 1) {
            this.sessions = sessions
          } else {
            this.sessions = this.sessions.concat(sessions)
          }
          this.hasMore = sessions.length >= this.pageSize
        } else {
          if (this.curPage === 1) {
            this.sessions = []
          }
          this.hasMore = false
        }
      } catch (error) {
        console.error('[ChannelHistoryChats] loadSessions error:', error)
      } finally {
        this.loading = false
        this.hasLoaded = true
      }
    },
    loadMore() {
      if (this.hasMore && !this.loading) {
        this.curPage++
        this.loadSessions()
      }
    },
    onScroll() {
      const scrollY = window.scrollY || document.documentElement.scrollTop
      const viewportHeight = window.innerHeight
      const fullHeight = document.documentElement.scrollHeight
      if (scrollY + viewportHeight >= fullHeight - 200 && this.hasMore && !this.loading) {
        this.loadMore()
      }
    },
    normalizeSession(s) {
      return {
        chatSessionId: String(s.chatSessionId || ''),
        fromUserId: String(s.fromUserId || ''),
        channelId: String(s.channelId || ''),
        aiSummary: s.aiSummary || '',
        requirementDetail: s.requirementDetail || '',
        userMsg: s.userMsg || '',
        fromUserName: s.fromUserName || '',
        fromUserAvatar: s.fromUserAvatar || '',
        public: Number(s.public || 0),
        matchPaused: Number(s.matchPaused || 0),
        createdAt: Number(s.createdAt || 0),
        publishedAt: Number(s.publishedAt || 0),
        status: Number(s.status || 0),
        requirementClarified: Number(s.requirementClarified || 0),
        chatSessionCommentCnt: Number(s.chatSessionCommentCnt || 0),
        matchCnt: Number(s.matchCnt || 0),
        likeCnt: Number(s.likeCnt || 0),
        expired: s.expired || '0',
        firstMediaContent: s.firstMediaContent || ''
      }
    },
    goToSearch() {
      this.$router.push({ path: '/channel-search/' + this.channelId, query: { onlyMine: '1' } })
    },
    goToSession(session) {
      this.$router.push({
        path: '/channel/' + this.channelId,
        query: { chatSessionId: session.chatSessionId }
      })
    }
  }
}
</script>

<style scoped>
.channel-history-page {
  background: #F7F5F2;
  min-height: 100vh;
}

.channel-history-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: white;
  border: 1px solid #E0DDD8;
  flex-shrink: 0;
}

.channel-history-title {
  font-size: 16px;
  font-weight: 600;
}

.channel-history-list {
  padding: 12px;
}

.channel-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 40px;
}
.channel-empty-icon {
  position: relative;
  width: 80px;
  height: 80px;
}
.channel-empty-icon-bg {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #E8543A;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 20px rgba(232, 84, 58, 0.25);
}
.channel-empty-icon-sparkle {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #FFF8F6;
  display: flex;
  align-items: center;
  justify-content: center;
}
.channel-empty-title {
  margin-top: 20px;
  font-size: 17px;
  font-weight: 600;
  color: #1A1A1A;
}
.channel-empty-subtitle {
  margin-top: 8px;
  font-size: 13px;
  color: #8A8A8A;
  text-align: center;
  line-height: 1.5;
}
.channel-empty-hint {
  margin-top: 6px;
  display: flex;
  align-items: flex-start;
  gap: 4px;
  padding: 5px 10px;
  background: #FEF0ED;
  border-radius: 10px;
  font-size: 11px;
  color: #E8543A;
  line-height: 1.4;
}
.channel-empty-hint-icon {
  margin-top: 1px;
  flex-shrink: 0;
}

.history-session-item {
  background: white;
  padding: 12px;
  border-radius: 16px;
  margin-bottom: 8px;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.10);
  text-align: left;
}

.history-session-usermsg {
  font-size: 14px;
  color: #1A1A1A;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.history-session-ai-summary {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  font-size: 11px;
  color: #8A8A8A;
  line-height: 1.4;
  margin-top: 4px;
}

.history-session-ai-summary.has-user-msg {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.history-session-ai-icon {
  font-size: 11px;
  flex-shrink: 0;
  margin-top: 1px;
}

.history-session-ai-text {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.history-session-badges {
  display: flex;
  gap: 6px;
  margin-top: 8px;
}

.history-badge-private {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 500;
  color: #8A8A8A;
  background: rgba(140, 140, 140, 0.1);
}

.history-badge-expired {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 500;
  color: #E05252;
  background: rgba(255, 77, 79, 0.1);
}

.history-session-footer {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
}

.history-session-avatar {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  object-fit: cover;
}

.history-session-avatar-placeholder {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background: #E8543A;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 10px;
  font-weight: bold;
}

.history-session-name {
  font-size: 12px;
  color: #8A8A8A;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.history-session-time {
  font-size: 11px;
  color: #bbb;
}
</style>



// WEBPACK FOOTER //
// src/components/ChannelHistoryChats.vue